import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { markAsRead } from '../../../app/store/slices/notificationSlice';
import { notificationStorage } from '../../utils/notificationStorage';
import { NotificationType, type Notification } from '../../../types/notification';
import { useNotificationStore } from '../../../app/store/useNotificationStore';
import { senderKeys } from '../../../api/senderApi';
import { useAcceptOfferRequestMutation, useRejectOfferRequestMutation } from '../../../api/senderApi3';
import { useMarkNotificationAsRead } from '../../../api/NotificationApi';
import {
  useApproveEditItemRequestMutation,
  useCancelEditItemRequestMutation,
  travellerKeys,
} from '../../../api/travellerApi2';
import {
  useAcceptOfferRequestMutation as useTravellerAcceptOfferRequestMutation,
  useRejectOfferRequestMutation as useTravellerRejectOfferRequestMutation,
} from '../../../api/travellerApi3';
import { useUserStore } from '../../../app/store/useUserStore';
import { UserRole, normalizeRole } from '../../../types/user';
import { isTravellerRole } from '../../utils/roleUtils';
import OfferResultDialog from '../ui/Dialog/OfferResultDialog';
import EditCartRequestDialog from '../ui/Dialog/EditCartRequestDialog';
import EditCartResultDialog from '../ui/Dialog/EditCartResultDialog';
import { DeclineOfferDialog } from '../ui/Dialog/DeclineOfferDialog';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useToastStore } from '../../../app/store/useToastStore';
import OfferNotificationToast from '../ui/Toast/OfferNotificationToast';

export const NotificationListener: React.FC = () => {
  const [currentNotification, setCurrentNotification] = useState<Notification | null>(null);
  const [offerNotifications, setOfferNotifications] = useState<Notification[]>([]);
  const [showApprovedDialog, setShowApprovedDialog] = useState(false);
  const [showRejectedDialog, setShowRejectedDialog] = useState(false);
  const [showDeclineOfferDialog, setShowDeclineOfferDialog] = useState(false);
  const [showEditCartDialog, setShowEditCartDialog] = useState(false);
  const [showEditApprovedDialog, setShowEditApprovedDialog] = useState(false);
  const [showEditRejectedDialog, setShowEditRejectedDialog] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const dispatch = useDispatch();
  const { openDetailsDialog } = useNotificationStore();
  const { user } = useUserStore();
  const { showToast } = useToastStore();

  // API mutations
  const senderAcceptMutation = useAcceptOfferRequestMutation();
  const senderRejectMutation = useRejectOfferRequestMutation();
  const travellerAcceptMutation = useTravellerAcceptOfferRequestMutation();
  const travellerRejectMutation = useTravellerRejectOfferRequestMutation();
  const approveEditItemMutation = useApproveEditItemRequestMutation();
  const cancelEditItemMutation = useCancelEditItemRequestMutation();
  const markNotificationAsReadMutation = useMarkNotificationAsRead();

  const isTraveler = isTravellerRole(user?.user_role);
  const userName = (currentNotification?.data?.displayName as string) || '';

  useEffect(() => {
    const handleSignalRNotification = (event: CustomEvent<Notification>) => {
      const notification = event.detail;

      // Refetch order details and show toast when order is placed under review
      if (notification.type === NotificationType.ORDER_UNDER_REVIEW && notification.data?.status === 'under_review') {
        const orderCode = notification.data?.order_code as string | undefined;
        queryClient.invalidateQueries({ queryKey: ['sender', 'orderDetails', orderCode] });
        if (orderCode) {
          queryClient.invalidateQueries({ queryKey: travellerKeys.orderDetails(orderCode) });
        }
        setOfferNotifications(prev => [...prev, notification]);
        return;
      }

      // Refetch my items / my travels so the listing reflects the expired offer immediately
      if (notification.type === NotificationType.OFFER_EXPIRED) {
        queryClient.invalidateQueries({ queryKey: senderKeys.allMyItems });
        queryClient.invalidateQueries({ queryKey: travellerKeys.itineraries(Number(user?.user_id)) });
      }

      // Show white toast for chat messages, chat room creation, and delivery status types
      const WHITE_TOAST_TYPES: string[] = [
        NotificationType.CHAT_MESSAGE,
        NotificationType.CHAT_ROOM_CREATED,
        NotificationType.ORDER_GROUP_CHAT,
        'sender_drop_off_confirmed',
        'receiver_checkout_confirmed',
        'traveler_pickup_confirmed',
        'traveler_delivery_checkout_confirmed',
        'dropoff_reminder',
        'pickup_reminder',
        NotificationType.SHIPMENT_OFFER_WITHDRAWN,
        NotificationType.OFFER_EXPIRY_REMINDER,
        NotificationType.OFFER_EXPIRED,
      ];
      if (WHITE_TOAST_TYPES.includes(notification.type as string)) {
        setOfferNotifications(prev => [...prev, notification]);
        return;
      }

      // Check if notification is actionable
      const isActionable = isActionableNotification(notification);
      if (isActionable) {
        const offerTypesWithRoleGate = [
          NotificationType.SHIPMENT_OFFER_REQUEST,
          NotificationType.OFFER_REQUEST_APPROVED,
          NotificationType.OFFER_REQUEST_REJECTED,
          NotificationType.SHIPMENT_OFFER_APPROVED,
          NotificationType.SHIPMENT_OFFER_REJECTED,
        ];
        // data.type role gate applies only to result notifications (approved/rejected),
        // not to shipment_offer_request — consistent with NotificationDropdown filtering
        const offerTypesWithDataTypeGate = [
          NotificationType.OFFER_REQUEST_APPROVED,
          NotificationType.OFFER_REQUEST_REJECTED,
          NotificationType.SHIPMENT_OFFER_APPROVED,
          NotificationType.SHIPMENT_OFFER_REJECTED,
        ];
        if (offerTypesWithRoleGate.includes(notification.type)) {
          const currentRole = isTraveler ? UserRole.TRAVELLER : UserRole.SENDER;
          // Check notification.user_role (set at enrichment time) first
          if (notification.user_role && notification.user_role !== currentRole) {
            return;
          }
          // Fallback: check data.type field used by some backend events (result notifications only)
          if (offerTypesWithDataTypeGate.includes(notification.type)) {
            const intendedRole = normalizeRole(notification.data?.type as string | undefined);
            if (intendedRole && intendedRole !== currentRole) {
              return;
            }
          }
        }

        setCurrentNotification(notification);

        // Show appropriate dialog based on notification type and role
        if (notification.type === NotificationType.OFFER_REQUEST_APPROVED) {
          // offer_request_approved — both traveler and sender
          setShowApprovedDialog(true);
        } else if (notification.type === NotificationType.SHIPMENT_OFFER_APPROVED) {
          // shipment_offer_approved — both traveler and sender
          setShowApprovedDialog(true);
        } else if (notification.type === NotificationType.OFFER_REQUEST_REJECTED && !isTraveler) {
          // offer_request_rejected — sender only
          setShowRejectedDialog(true);
        } else if (notification.type === NotificationType.SHIPMENT_OFFER_REJECTED) {
          // shipment_offer_rejected — both traveler and sender
          setShowRejectedDialog(true);
        } else if (notification.type === NotificationType.EDIT_CART_REQUEST && isTraveler) {
          setShowEditCartDialog(true);
        } else if (notification.type === NotificationType.SHIPMENT_OFFER_REQUEST) {
          setOfferNotifications(prev => [...prev, notification]);
        }
      }
    };

    const handleKycStatusChange = (event: CustomEvent<{ status: string; message: string; type: string }>) => {
      const { message, type } = event.detail;
      showToast(message, type as Parameters<typeof showToast>[1], 8000);
    };

    window.addEventListener('signalr-notification', handleSignalRNotification as EventListener);
    window.addEventListener('kyc-status-changed', handleKycStatusChange as EventListener);

    return () => {
      window.removeEventListener('signalr-notification', handleSignalRNotification as EventListener);
      window.removeEventListener('kyc-status-changed', handleKycStatusChange as EventListener);
    };
  }, [isTraveler, showToast]);

  // Determine if notification requires action
  const isActionableNotification = (notification: Notification): boolean => {
    const actionableTypes = [
      NotificationType.SHIPMENT_OFFER_REQUEST,
      NotificationType.OFFER_REQUEST_APPROVED,
      NotificationType.OFFER_REQUEST_REJECTED,
      NotificationType.SHIPMENT_OFFER_APPROVED,
      NotificationType.SHIPMENT_OFFER_REJECTED,
      NotificationType.EDIT_CART_REQUEST,
    ];

    return actionableTypes.includes(notification.type);
  };

  // Remove offer notification from the list
  const removeOfferNotification = (notificationId: string) => {
    setOfferNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  // Handle decline offer submission with reason
  const handleDeclineOfferSubmit = async (reason: string) => {
    if (!currentNotification) return;

    try {
      const orderCode = currentNotification.data?.order_code;
      if (!orderCode) {
        showToast('Missing order information', 'error');
        return;
      }

      // Call the appropriate reject mutation based on user role
      if (isTraveler) {
        await travellerRejectMutation.mutateAsync({
          order_code: orderCode,
          reason: reason
        });
      } else {
        await senderRejectMutation.mutateAsync({
          order_code: orderCode,
          reason: reason
        });
      }

      // Mark notification as rejected in storage
      const notifications = notificationStorage.getNotifications();
      const notif = notifications.find((n) => n.id === currentNotification.id);
      if (notif) {
        notif.isRead = true;
        notif.data = { ...notif.data, statusFlag: 'rejected' };
        const updated = notifications.map((n) => (n.id === currentNotification.id ? notif : n));
        localStorage.setItem('skyporter_notifications', JSON.stringify(updated));
        window.dispatchEvent(new CustomEvent('notification-state-changed'));
      }

      dispatch(markAsRead(currentNotification.id));
      if (currentNotification.data?.notification_id) {
        markNotificationAsReadMutation.mutate(currentNotification.data.notification_id);
      }

      // Close dialog
      setShowDeclineOfferDialog(false);
      setCurrentNotification(null);

      // Invalidate queries to refresh data
      queryClient.invalidateQueries();

      showToast('Offer rejected successfully', 'success');
    } catch (error: any) {
      // Toast is already shown by API interceptor for 4xx errors
    }
  };

  // Close decline offer dialog
  const closeDeclineOfferDialog = () => {
    setShowDeclineOfferDialog(false);
    setCurrentNotification(null);
  };


  // Helper function to mark notification as done
  const markNotificationAsDone = (notificationId: string) => {
    const notifications = notificationStorage.getNotifications();
    const notif = notifications.find((n) => n.id === notificationId);

    if (notif) {
      notif.isRead = true;
      notif.data = {
        ...notif.data,
        statusFlag: 'done',
      };

      // Save updated notifications
      const updated = notifications.map((n) => (n.id === notificationId ? notif : n));
      localStorage.setItem('skyporter_notifications', JSON.stringify(updated));

      // Dispatch state change event
      window.dispatchEvent(new CustomEvent('notification-state-changed'));


    }
  };


  const closeApprovedDialog = () => {
    setShowApprovedDialog(false);
    setCurrentNotification(null);
  };

  const closeRejectedDialog = () => {
    setShowRejectedDialog(false);
    setCurrentNotification(null);
  };

const handleViewDetailsFromApproved = () => {
  const orderCode = currentNotification?.data?.order_code;
  closeApprovedDialog();
  if (orderCode) {
    if (isTraveler) {
      navigate(`/traveller/order-details/${orderCode}`);
    } else {
      navigate(`/order-details/${orderCode}`);
    }
  }
};

const handleViewTravelersFromRejected = () => {
  closeRejectedDialog();
  navigate('/');
};

const handleViewSendersFromRejected = () => {
  closeRejectedDialog();
  navigate('/');
};

const handleViewTravelerFromApproved = () => {
  const orderCode = currentNotification?.data?.order_code;
  closeApprovedDialog();
  if (orderCode) {
    navigate(`/order-details/${orderCode}`); // sender route
  }
};

  // Handle approve edit request
  const handleApproveEditRequest = async () => {
    if (!currentNotification) return;

    try {
      // Support both snake_case and camelCase field names
      const items = currentNotification.data?.items;
      const itemId = currentNotification.data?.item_id || items?.[0]?.id;
      const orderCode = currentNotification.data?.order_code || currentNotification.data?.orderCode;

      if (!itemId || !orderCode) {
        showToast('Missing item or order information. Please try again.', 'error');
        return;
      }

      await approveEditItemMutation.mutateAsync({
        item_id: itemId,
        order_code: orderCode,
      });

      // Mark notification as done
      markNotificationAsDone(currentNotification.id);

      // Close edit cart dialog
      setShowEditCartDialog(false);

      // Invalidate queries to refresh data
      queryClient.invalidateQueries();

      // Show success dialog
      setShowEditApprovedDialog(true);
    } catch (error: any) {
    }
  };

  // Handle reject edit request
  const handleRejectEditRequest = async () => {
    if (!currentNotification) return;

    try {
      // Support both snake_case and camelCase field names
      const items = currentNotification.data?.items;
      const itemId = currentNotification.data?.item_id || items?.[0]?.id;
      const orderCode = currentNotification.data?.order_code || currentNotification.data?.orderCode;

      if (!itemId || !orderCode) {
        showToast('Missing item or order information. Please try again.', 'error');
        return;
      }

      await cancelEditItemMutation.mutateAsync({
        item_id: itemId,
        order_code: orderCode,
      });

      // Mark notification as done ONLY after successful API call
      markNotificationAsDone(currentNotification.id);

      // Close edit cart dialog
      setShowEditCartDialog(false);

      // Invalidate queries to refresh data
      queryClient.invalidateQueries();

      // Show rejection dialog
      setShowEditRejectedDialog(true);
    } catch (error: any) {
      // Toast is already shown by API interceptor for 4xx errors
    }
  };

  // Close edit cart dialogs
  const closeEditCartDialog = () => {
    setShowEditCartDialog(false);
  };

  const closeEditApprovedDialog = () => {
    setShowEditApprovedDialog(false);
    setCurrentNotification(null);
  };

  const closeEditRejectedDialog = () => {
    setShowEditRejectedDialog(false);
    setCurrentNotification(null);
  };

  const handleViewDetailsFromEditApproved = () => {
    const orderCode = currentNotification?.data?.order_code || currentNotification?.data?.orderCode;
    closeEditApprovedDialog();
    if (orderCode) {
      navigate(`/my-orders/${orderCode}`);
    }
  };

  return (
    <>
      {/* Offer notifications as toasts */}
      {offerNotifications.map((notification, index) => (
        <OfferNotificationToast
          key={notification.id}
          notification={notification}
          index={index}
          isTraveler={isTraveler}
          onClose={() => removeOfferNotification(notification.id)}
        />
      ))}

      {/* Offer Approved Dialog */}
      {showApprovedDialog && (
        <OfferResultDialog
          open={showApprovedDialog}
          onClose={closeApprovedDialog}
          variant="accepted"
          role={isTraveler ? 'traveler' : 'sender'}
          onAction={isTraveler ? handleViewDetailsFromApproved : handleViewTravelerFromApproved}
          orderDetails={{
            orderCode: currentNotification?.data?.order_code,
            name: userName,
            itineraryName: currentNotification?.data?.itinerary_name || currentNotification?.data?.itineraryName,
            itemName: currentNotification?.data?.shipment_name || currentNotification?.data?.shipmentName,
            categoryName: Array.isArray(currentNotification?.data?.category_names)
              ? currentNotification.data.category_names.join(', ')
              : currentNotification?.data?.category_name || currentNotification?.data?.categoryName,
          }}
        />
      )}

      {/* Offer Rejected Dialog */}
      {showRejectedDialog && (
        <OfferResultDialog
          open={showRejectedDialog}
          onClose={closeRejectedDialog}
          variant="rejected"
          role={isTraveler ? 'traveler' : 'sender'}
          onAction={isTraveler ? handleViewSendersFromRejected : handleViewTravelersFromRejected}
          orderDetails={{
            name: userName,
            itineraryName: currentNotification?.data?.itinerary_name || currentNotification?.data?.itineraryName || (Array.isArray(currentNotification?.data?.category_names) ? currentNotification.data.category_names.join(', ') : undefined),
            shipmentName: currentNotification?.data?.shipment_name || currentNotification?.data?.shipmentName || (Array.isArray(currentNotification?.data?.category_names) ? currentNotification.data.category_names.join(', ') : undefined),
            reason: currentNotification?.data?.rejection_reason || currentNotification?.data?.reason,
          }}
        />
      )}

      {/* Edit Cart Request Dialog - Traveler Only */}
      {isTraveler && showEditCartDialog && (() => {
        // Extract item data from the notification
        const items = currentNotification?.data?.items;
        const itemId = currentNotification?.data?.item_id;
        const item = items?.find((i: any) => i.id === itemId) || items?.[0];

        // Support both snake_case (order_code) and camelCase (orderCode)
        const orderCode = currentNotification?.data?.order_code || currentNotification?.data?.orderCode;
        const senderName = currentNotification?.data?.sender_name || currentNotification?.data?.name;

        return (
          <EditCartRequestDialog
            open={showEditCartDialog}
            onClose={closeEditCartDialog}
            onApprove={handleApproveEditRequest}
            onReject={handleRejectEditRequest}
            editDetails={{
              item_id: itemId || item?.id,
              order_code: orderCode,
              senderName: senderName,
              oldValues: {
                category: currentNotification?.data?.old_category || item?.category,
                weight: currentNotification?.data?.old_weight || item?.weight,
                price: currentNotification?.data?.old_price || item?.price,
                pieces: currentNotification?.data?.old_pieces || item?.pieces,
                pages: currentNotification?.data?.old_pages || item?.pages,
              },
              newValues: {
                category: currentNotification?.data?.new_category || item?.category,
                weight: currentNotification?.data?.new_weight || item?.edit_weight,
                price: currentNotification?.data?.new_price || item?.edit_price,
                pieces: currentNotification?.data?.new_pieces || item?.edit_pieces,
                pages: currentNotification?.data?.new_pages || item?.edit_pages,
              },
            }}
            isLoading={approveEditItemMutation.isPending || cancelEditItemMutation.isPending}
          />
        );
      })()}

      {/* Edit Cart Approved Dialog */}
      {isTraveler && showEditApprovedDialog && (
        <EditCartResultDialog
          open={showEditApprovedDialog}
          onClose={closeEditApprovedDialog}
          variant="approved"
          onViewDetails={handleViewDetailsFromEditApproved}
          orderDetails={{
            orderCode: currentNotification?.data?.order_code || currentNotification?.data?.orderCode,
            senderName: currentNotification?.data?.sender_name || currentNotification?.data?.name,
          }}
        />
      )}

      {/* Edit Cart Rejected Dialog */}
      {isTraveler && showEditRejectedDialog && (
        <EditCartResultDialog
          open={showEditRejectedDialog}
          onClose={closeEditRejectedDialog}
          variant="rejected"
          orderDetails={{
            orderCode: currentNotification?.data?.order_code || currentNotification?.data?.orderCode,
            senderName: currentNotification?.data?.sender_name || currentNotification?.data?.name,
          }}
        />
      )}

      {/* Decline Offer Dialog - For both sender and traveler when rejecting from notification */}
      <DeclineOfferDialog
        open={showDeclineOfferDialog}
        onClose={closeDeclineOfferDialog}
        onSubmit={handleDeclineOfferSubmit}
        isLoading={travellerRejectMutation.isPending || senderRejectMutation.isPending}
        userType={isTraveler ? 'traveler' : 'sender'}
      />
    </>
  );
};
